import { Box, Container, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Building2, CheckCircle, Star, Home } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const nswStats = [
  { number: '500+', label: 'Homes Completed' },
  { number: '6+', label: 'Years in NSW' },
  { number: '100%', label: 'Licensed & Compliant' },
];

const australiaCapabilities = [
  'Deliver land & build packages nationwide',
  'Manage build‑only contracts across multiple states',
  'Complete granny flats, renovations, and extensions',
  'Undertake complex and multi‑storey residential builds',
  'Maintain consistent construction quality across locations',
];

const commitments = [
  { icon: Star,      label: 'Quality workmanship' },
  { icon: CheckCircle, label: 'Clear communication' },
  { icon: Building2, label: 'Professional project management' },
  { icon: Home,      label: 'Custom residential solutions' },
];

function WhereWeBuild() {
  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f8f8', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        backgroundColor: 'var(--primary-color)',
        py: { xs: 7, md: 11 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-80px', right: '-80px',
          width: '320px', height: '320px',
          borderRadius: '50%',
          border: '70px solid rgba(192,174,93,0.07)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: '-90px', left: '-50px',
          width: '280px', height: '280px',
          borderRadius: '50%',
          border: '55px solid rgba(192,174,93,0.05)',
          pointerEvents: 'none',
        },
      }}>
        <Typography sx={{ fontSize: { xs: '32px', md: '52px' }, fontWeight: 800, color: '#fff', letterSpacing: 2, lineHeight: 1.1 }}>
          WHERE WE BUILD
        </Typography>
        <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2.5 }} />
        <Typography sx={{ fontSize: { xs: '15px', md: '17px' }, color: 'rgba(255,255,255,0.65)', maxWidth: 600, mx: 'auto', lineHeight: 1.85, px: 2 }}>
          Building Across NSW & Major States of Australia
        </Typography>
      </Box>

      {/* ── GOLD NOTICE BAR ── */}
      <Box sx={{ backgroundColor: 'var(--primary-text-color)', py: 2 }}>
        <Container maxWidth="xl">
          <Typography sx={{ fontSize: '13px', color: 'var(--primary-color)', fontWeight: 700, textAlign: 'center' }}>
            🏠 Clarence Homes – Trusted builders across NSW and Australia
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>

        {/* ── INTRO CARD ── */}
        <Box
          data-aos="fade-up"
          sx={{
            backgroundColor: 'var(--primary-color)',
            borderRadius: '12px',
            p: { xs: 3, md: 5 },
            mb: 6,
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""', position: 'absolute',
              bottom: 0, right: 0,
              width: '160px', height: '160px',
              borderRadius: '50%',
              backgroundColor: 'rgba(192,174,93,0.07)',
              transform: 'translate(40%, 40%)',
            }
          }}
        >
          <Box sx={{ width: '40px', height: '4px', backgroundColor: 'var(--primary-text-color)', mb: 3 }} />
          <Typography sx={{ fontSize: { xs: '20px', md: '26px' }, fontWeight: 800, color: '#fff', mb: 2 }}>
            Nationwide Residential Building
          </Typography>
          <Typography sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.95 }}>
            Clarence Homes proudly delivers residential building projects across New South Wales, with extensive experience throughout Sydney and surrounding regions.
          </Typography>
          <Typography sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.95, mt: 2 }}>
            In addition, we are licensed to build in all major states across Australia, allowing us to undertake residential projects nationwide. This means our clients benefit from consistent quality, professional standards, and proven systems — no matter the location.
          </Typography>
        </Box>

        {/* ── NSW SPECIALISTS ── */}
        <Box
          data-aos="fade-up"
          sx={{
            display: 'flex', flexDirection: { xs: 'column', md: 'row' },
            gap: 4, mb: 6,
          }}
        >
          {/* Left text */}
          <Box sx={{
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: '12px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
            borderLeft: '5px solid var(--primary-text-color)',
          }}>
            <Typography sx={{ fontSize: '11px', color: 'var(--primary-text-color)', fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', mb: 1 }}>
              Core Market
            </Typography>
            <Typography sx={{ fontSize: { xs: '20px', md: '24px' }, fontWeight: 800, color: 'var(--primary-color)', mb: 2 }}>
              New South Wales Specialists
            </Typography>
            <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95 }}>
              NSW remains our core market, where we have successfully completed over 500 residential homes. Our in‑depth understanding of local council requirements, CDC and DA approval processes, and site conditions allows us to deliver projects efficiently and with confidence.
            </Typography>
          </Box>

          {/* Right stats */}
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 2,
            minWidth: { md: '220px' },
          }}>
            {nswStats.map((stat, i) => (
              <Box
                key={i}
                data-aos="zoom-in"
                data-aos-delay={i * 80}
                sx={{
                  flex: 1,
                  backgroundColor: 'var(--primary-color)',
                  borderRadius: '12px',
                  p: 3,
                  textAlign: 'center',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: { xs: '32px', md: '38px' }, fontWeight: 800, color: 'var(--primary-text-color)', lineHeight: 1 }}>
                  {stat.number}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', mt: 0.5, fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── AUSTRALIA-WIDE CAPABILITY ── */}
        <Box
          data-aos="fade-up"
          sx={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
            mb: 6,
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {/* Navy strip */}
          <Box sx={{
            width: { xs: '100%', sm: '90px' },
            backgroundColor: 'var(--primary-color)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            py: { xs: 2.5, sm: 0 }, gap: 1.5, flexShrink: 0,
          }}>
            <Building2 size={28} color="var(--primary-text-color)" />
            <Typography sx={{ fontSize: '20px', fontWeight: 800, color: 'rgba(255,255,255,0.18)', lineHeight: 1 }}>02</Typography>
          </Box>
          <Box sx={{ p: { xs: 3, md: 4.5 }, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ width: '4px', height: '22px', backgroundColor: 'var(--primary-text-color)', borderRadius: '2px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 700, color: 'var(--primary-color)' }}>
                Australia‑Wide Capability
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.9, mb: 2.5 }}>
              Being licensed across major Australian states enables Clarence Homes to:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {australiaCapabilities.map((item, i) => (
                <Box key={i} data-aos="fade-left" data-aos-delay={i * 60} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{
                    mt: '4px', width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: 'var(--primary-color)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CheckCircle size={12} color="var(--primary-text-color)" />
                  </Box>
                  <Typography sx={{ fontSize: '15px', color: '#444', lineHeight: 1.7 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.9, mt: 2.5, fontStyle: 'italic' }}>
              Whether your project is in NSW or interstate, our systems, experience, and construction standards remain the same.
            </Typography>
          </Box>
        </Box>

        {/* ── ONE BUILDER NATIONWIDE ── */}
        <Box data-aos="fade-up" sx={{ mb: 6 }}>
          <Typography sx={{ fontSize: '11px', color: 'var(--primary-text-color)', fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', mb: 1 }}>
            Our Promise
          </Typography>
          <Typography sx={{ fontSize: { xs: '22px', md: '28px' }, fontWeight: 800, color: 'var(--primary-color)', mb: 3.5 }}>
            One Builder. Nationwide Reach.
          </Typography>
          <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.9, mb: 4, maxWidth: 700 }}>
            No matter where you're building, Clarence Homes offers the same commitment to:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {commitments.map(({ icon: Icon, label }, i) => (
              <Box
                key={i}
                data-aos="zoom-in"
                data-aos-delay={i * 70}
                sx={{
                  flex: '1 1 200px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  p: 3,
                  boxShadow: '0 2px 16px rgba(19,25,83,0.07)',
                  display: 'flex', alignItems: 'center', gap: 2,
                  border: '1.5px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'var(--primary-text-color)',
                    transform: 'translateY(-6px)',
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    '& .commit-icon': { backgroundColor: 'var(--primary-color)' },
                    '& svg': { color: 'var(--primary-text-color)', transition: 'color 0.3s' },
                  }
                }}
              >
                <Box className="commit-icon" sx={{
                  width: 60, height: 60, borderRadius: '10px', flexShrink: 0,
                  backgroundColor: 'rgba(19,25,83,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background-color 0.3s',
                  '& svg': { color: 'var(--primary-color)', transition: 'color 0.3s' },
                  '&:hover svg': { color: 'var(--primary-text-color)' },
                }}>
                  <Icon size={22} />
                </Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'var(--primary-color)' }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── CTA BANNER ── */}
        <Box
          data-aos="fade-up"
          sx={{
            borderRadius: '12px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, var(--primary-color) 0%, #1e2a6e 100%)',
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""', position: 'absolute',
              top: '-40px', right: '-40px',
              width: '200px', height: '200px',
              borderRadius: '50%',
              border: '50px solid rgba(192,174,93,0.08)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ width: '60px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mb: 3 }} />
          <Typography sx={{ fontSize: { xs: '22px', md: '30px' }, fontWeight: 800, color: '#fff', mb: 2 }}>
            Build With Confidence, Anywhere in Australia
          </Typography>
          <Typography sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.9, maxWidth: 620, mx: 'auto' }}>
            If you're planning a residential project in NSW or any major Australian state, Clarence Homes has the licensing, experience, and expertise to deliver.
          </Typography>
        </Box>

      </Container>
    </Box>
  );
}

export default WhereWeBuild;
